"use client";

import Script from "next/script";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import { trackEvent } from "@/lib/track";

// Plausible CE self-hostato (privacy-friendly, cookieless).
const PLAUSIBLE_SRC =
  "https://stats.dimensione4.it/js/pa-74W-dogLrLbuiw3Sff9G9.js";

/**
 * Bootstraps Plausible and tracks scroll depth milestones once per page view.
 *
 * Niente GA qui: caricato senza consenso sarebbe una violazione. Se serve,
 * usare <GoogleAnalytics /> che aspetta il consenso del banner.
 */
export function Analytics() {
  const pathname = usePathname();
  const depthsFiredRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    depthsFiredRef.current = new Set();

    // Cache the document height between resize/orientation changes so the
    // scroll handler does not have to read `scrollHeight` on every tick.
    // Reading `scrollHeight` forces layout (it shows up in Lighthouse's
    // forced-reflow trace), and the document size is effectively static
    // between layout-changing events.
    let docHeight = 0;
    let scheduled = false;

    const measure = () => {
      docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
    };

    const scheduleMeasure = () => {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(() => {
        measure();
        scheduled = false;
      });
    };

    const onScroll = () => {
      if (docHeight <= 0) return;
      const depth = Math.round((window.scrollY / docHeight) * 100);
      for (const target of [50, 90] as const) {
        if (depth >= target && !depthsFiredRef.current.has(target)) {
          depthsFiredRef.current.add(target);
          trackEvent(`scroll_${target}`, { page: pathname });
        }
      }
    };

    // Initial measurement happens after first paint so we don't race
    // hydration-time layout.
    scheduleMeasure();

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", scheduleMeasure, { passive: true });
    window.addEventListener("orientationchange", scheduleMeasure, {
      passive: true,
    });

    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("orientationchange", scheduleMeasure);
    };
  }, [pathname]);

  return (
    <>
      {/* Plausible CE self-hostato (cookieless): pageview automatici. La config
          del sito è nell'hash del filename, quindi niente data-domain. */}
      <Script src={PLAUSIBLE_SRC} strategy="afterInteractive" />
      <Script id="plausible-init" strategy="afterInteractive">
        {`window.plausible=window.plausible||function(){(plausible.q=plausible.q||[]).push(arguments)},plausible.init=plausible.init||function(i){plausible.o=i||{}};plausible.init()`}
      </Script>
    </>
  );
}
